import type { Track } from '@/world/Track';
import type { TrackFeatureSpec, TrackFeatureKind, TrackFeatureStyle } from '@/config/cities';
import type { WallResolution } from '@/physics/collisions';
import { tuning } from '@/config/tuning';

/**
 * trackFeatures.ts — PURE physics for the interactive course features (M47):
 * BOOST pads, SLICK patches, and static OBSTACLES. No three.js, no DOM. Specs
 * are hand-placed per city (config/cities.ts); `placeFeatures` freezes them
 * into world-space shapes once per track, and the sim queries them every step
 * — all deterministic, so offline and the online authoritative sim agree.
 */

/** A feature resolved into frozen world space (dir/right captured at placement). */
export interface PlacedFeature {
  kind: TrackFeatureKind;
  style: TrackFeatureStyle;
  x: number;
  y: number;
  z: number;
  /** Track forward at the feature (unit). */
  dirX: number;
  dirZ: number;
  /** Track right at the feature (unit). */
  rightX: number;
  rightZ: number;
  /** boost/slick: half-extent along the track. */
  halfLength: number;
  /** boost/slick: lateral half-width. */
  halfWidth: number;
  /** obstacle: collision circle radius. */
  radius: number;
  /** obstacle: how much road is left between it and the NEARER guardrail
   *  (M143c). A body wider than this cannot pass on that side — see
   *  resolveObstacle. */
  wallGap: number;
  /** obstacle: unit vector from it toward the middle of the road — the side
   *  that is always open. (0,0) for one standing on the centreline. */
  inX: number;
  inZ: number;
  /** ford: lateral centre of the dry PLANK window, relative to the feature. */
  plankLateral: number;
  /** ford: half-width of that dry window. */
  plankHalfWidth: number;
}

/** Resolve a city's feature specs onto a generated track (once per race). */
export function placeFeatures(track: Track, specs: readonly TrackFeatureSpec[]): PlacedFeature[] {
  return specs.map((f) => {
    const s = track.sampleAt(f.at * track.finishDistance);
    const x = s.x + s.rightX * f.lateral;
    const z = s.z + s.rightZ * f.lateral;
    const radius = f.radius ?? 1.8;
    const side = Math.sign(f.lateral);
    return {
      kind: f.kind,
      style: f.style,
      x,
      y: track.heightAt(x, z),
      z,
      dirX: Math.sin(s.dir), // §6.1: heading → forward (sin h, cos h)
      dirZ: Math.cos(s.dir),
      rightX: s.rightX,
      rightZ: s.rightZ,
      halfLength: (f.length ?? 8) / 2,
      halfWidth: f.halfWidth ?? 4,
      radius,
      wallGap: track.halfWidth - (Math.abs(f.lateral) + radius),
      inX: -side * s.rightX,
      inZ: -side * s.rightZ,
      plankLateral: f.plankLateral ?? 0,
      plankHalfWidth: f.plankHalfWidth ?? 6,
    };
  });
}

/** Is a point inside a boost/slick strip? (Planar rect via dot products.) */
export function inStrip(px: number, pz: number, f: PlacedFeature): boolean {
  const dx = px - f.x;
  const dz = pz - f.z;
  if (Math.abs(dx * f.dirX + dz * f.dirZ) > f.halfLength) return false;
  return Math.abs(dx * f.rightX + dz * f.rightZ) <= f.halfWidth;
}

/**
 * nyc-lake — THE FORD. Is a point in the WATER of a ford, i.e. inside the
 * band AND wide of the plank crossing? A point on the plank is dry and takes
 * nothing at all.
 *
 * This is a pure function of POSITION and the feature's frozen frame — no
 * timers, no per-step latches — so an online client re-derives exactly the
 * same answer from any snapshot, the same contract `slick` has always had.
 */
export function inFordWater(px: number, pz: number, f: PlacedFeature): boolean {
  const dx = px - f.x;
  const dz = pz - f.z;
  if (Math.abs(dx * f.dirX + dz * f.dirZ) > f.halfLength) return false;
  const lat = dx * f.rightX + dz * f.rightZ;
  if (Math.abs(lat) > f.halfWidth) return false;
  return Math.abs(lat - f.plankLateral) > f.plankHalfWidth;
}

/**
 * Car params while WADING a ford. Its own scale, separate from `slick`: a
 * shallow crossing is not ice. The loss of bite is mild and deliberate — what
 * the player should feel is the DRAG (tuning.features.ford.carDrag), because
 * "slow down crossing the water portion" is the owner's whole spec. Memoized
 * per base params object, like slickParamsFor, so the hot loop never allocates.
 */
const fordCache = new Map<object, typeof tuning.car>();
export function fordParamsFor(base: typeof tuning.car): typeof tuning.car {
  let p = fordCache.get(base);
  if (!p) {
    const g = base.lateralGrip * tuning.features.ford.gripScale;
    p = { ...base, lateralGrip: g, driftGrip: Math.min(base.driftGrip, g) };
    fordCache.set(base, p);
  }
  return p;
}

/**
 * Body (car circle / ball sphere) vs a static obstacle circle — the DIVERTER
 * version (M51, owner rework): the body is pushed clear and its velocity is
 * redirected TANGENTIALLY, sliding it around the obstacle with `keep` of its
 * speed, instead of bouncing it backward. Obstacles interrupt your line, never
 * your race. Returns null when clear (the common case). Only the body moves —
 * the obstacle is city furniture.
 *
 * NO SQUEEZING PAST ON THE WALL SIDE (M143c). An obstacle parked near a
 * guardrail leaves a pocket between itself and the rail. When that pocket is
 * narrower than the body, sliding "around the outside" is not a way past: the
 * rail pushes the body back in, the obstacle pushes it back out, and it stays
 * there. A player backs out; a car driven by the AI did not — measured, in
 * New York (two of the Times Square cabs, and the roadworks taper) about one
 * AI car in twelve sat in such a pocket at full throttle for the REST OF THE
 * RACE. Online that is a race which cannot end, because one of those cars can
 * be a disconnected friend's. So in a pocket the body is shunted the other
 * way, across the back of the obstacle toward the open road — still a
 * sideways deflection, still most of its speed (the M51 rule), and it now
 * gets past.
 */
export function resolveObstacle(
  px: number,
  pz: number,
  radius: number,
  f: PlacedFeature,
  vx: number,
  vz: number,
  keep: number,
): WallResolution | null {
  const dx = px - f.x;
  const dz = pz - f.z;
  const d = Math.hypot(dx, dz);
  const minD = radius + f.radius;
  if (d >= minD || d < 1e-6) return null;
  const nx = dx / d;
  const nz = dz / d;
  const over = minD - d;
  const into = -(vx * nx + vz * nz);
  let nvx = vx;
  let nvz = vz;
  if (into > 0) {
    // Which way around? Follow the existing tangential motion; a dead-centre
    // hit picks a side deterministically from the approach cross product.
    const tx = -nz;
    const tz = nx;
    let s = vx * tx + vz * tz;
    if (Math.abs(s) < 1e-3) s = vx * nz - vz * nx >= 0 ? 1 : -1;
    const sign = Math.sign(s) || 1;
    const speed = Math.hypot(vx, vz);
    // The slide direction: the tangent the body is already following…
    let sx = tx * sign;
    let sz = tz * sign;
    // …unless that leads into a pocket it cannot fit through (see above):
    // then straight toward the open side of the road.
    if (f.wallGap < 2 * radius && sx * f.inX + sz * f.inZ < 0) {
      sx = f.inX;
      sz = f.inZ;
    }
    // Mostly along the slide + a whisper outward so it doesn't re-graze every step.
    let ddx = sx * 0.94 + nx * 0.34;
    let ddz = sz * 0.94 + nz * 0.34;
    const m = Math.hypot(ddx, ddz) || 1;
    ddx /= m;
    ddz /= m;
    nvx = ddx * speed * keep;
    nvz = ddz * speed * keep;
  }
  return { pushX: nx * over, pushZ: nz * over, vx: nvx, vz: nvz };
}

/**
 * Car params while ON a slick patch: lateral grip collapses so the car slides
 * (Mario-Kart ice / a monsoon puddle). Memoized per base params object so the
 * hot sim loop never allocates. NOTE: the M40 human wall-feel is gated by
 * `params === tuning.car`, so a human ON a slick briefly loses the wall-assist
 * — sliding along a rail on ice is the honest outcome anyway.
 */
const slickCache = new Map<object, typeof tuning.car>();
export function slickParamsFor(base: typeof tuning.car): typeof tuning.car {
  let p = slickCache.get(base);
  if (!p) {
    p = {
      ...base,
      lateralGrip: base.lateralGrip * tuning.features.slickGripScale,
      driftGrip: Math.min(base.driftGrip, base.lateralGrip * tuning.features.slickGripScale),
    };
    slickCache.set(base, p);
  }
  return p;
}
